import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Landmark, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, SimpleSelect } from "@/components/wizard/Field";
import { FileUpload } from "@/components/FileUpload";
import { L, RateBadge, TaxBadge } from "./common";
import { t, getLang } from "@/i18n";

const CountryPicker = ({ countries, value, onChange }) => {
  const [q, setQ] = useState("");
  const lang = getLang();
  const list = useMemo(() => countries.filter((c) => !q || c[lang].toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase() === q.toLowerCase()).sort((a, b) => a[lang].localeCompare(b[lang])), [countries, q, lang]);
  return (
    <div className="space-y-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar país…")} data-testid="country-search" />
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} size={6} data-testid="country-select"
        className="w-full rounded-md border border-input bg-background text-sm p-1 focus:outline-none focus:ring-2 focus:ring-ring">
        {list.map((c) => <option key={c.code} value={c.code} className="px-2 py-1 rounded">{c[lang]} {c.rate === 0 ? "· 0%" : c.treaty ? `· ${c.rate}%` : ""}</option>)}
      </select>
    </div>
  );
};

export const PaymentProfileForm = ({ onSaved }) => {
  const [countries, setCountries] = useState([]);
  const [p, setP] = useState(null);
  const [form, setForm] = useState({ country: "MX", entity_type: "individual", legal_name: "", tax_id: "", address: "", bank: {}, tax_form_file_id: null });
  const [taxFile, setTaxFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/payments/countries").then((r) => setCountries(r.data));
    api.get("/payments/profile").then((r) => { if (r.data) { setP(r.data); setForm({ country: r.data.country, entity_type: r.data.entity_type, legal_name: r.data.legal_name, tax_id: r.data.tax_id || "", address: r.data.address || "", bank: r.data.bank || {}, tax_form_file_id: r.data.tax_form_file_id || null }); } });
  }, []);

  const country = countries.find((c) => c.code === form.country);
  const isUS = country?.is_us;
  const formName = isUS ? "W-9" : form.entity_type === "company" ? "W-8BEN-E" : "W-8BEN";
  const formUrl = { "W-9": "https://www.irs.gov/pub/irs-pdf/fw9.pdf", "W-8BEN": "https://www.irs.gov/pub/irs-pdf/fw8ben.pdf", "W-8BEN-E": "https://www.irs.gov/pub/irs-pdf/fw8bene.pdf" }[formName];
  const err = (k) => errors.find((e) => e.field === k)?.message;
  const setBank = (k, v) => setForm((f) => ({ ...f, bank: { ...f.bank, [k]: v } }));

  const save = async () => {
    setSaving(true); setErrors([]);
    try {
      const { data } = await api.put("/payments/profile", form);
      setP(data); setTaxFile(null); toast.success(t("Datos de pago guardados")); onSaved?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) { setErrors(d.errors); toast.error(t("Revisa los campos marcados")); } else toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  if (!countries.length) return null;
  return (
    <div className="space-y-6" data-testid="payment-profile-form">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <Field label="País de residencia fiscal" hint="Determina el formulario fiscal y la retención de EE. UU. sobre regalías.">
            <CountryPicker countries={countries} value={form.country} onChange={(country) => setForm((f) => ({ ...f, country, bank: {} }))} />
          </Field>
          {country && (
            <div className="panel p-4 space-y-2" data-testid="country-summary">
              <p className="font-medium">{L({ es: country.es, en: country.en })}</p>
              <p className="text-sm text-muted-foreground">{t("Retención EE. UU. sobre regalías:")} <RateBadge rate={country.rate} treaty={country.treaty} testId="country-rate" /></p>
              <p className="text-xs text-muted-foreground">{country.treaty ? t("Existe tratado fiscal con Estados Unidos: se aplica la tasa reducida al entregar el formulario.") : t("Sin tratado fiscal con Estados Unidos: aplica la retención estándar del 30%.")}</p>
            </div>
          )}
        </div>
        <div className="lg:col-span-7 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tipo de beneficiario">
              <SimpleSelect value={form.entity_type} onChange={(entity_type) => setForm((f) => ({ ...f, entity_type }))} options={[["individual", "Persona física"], ["company", "Empresa / persona moral"]]} testId="entity-type" />
            </Field>
            <Field label="Nombre legal completo" hint="Tal como aparece en tu identificación o acta constitutiva.">
              <Input value={form.legal_name} onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))} data-testid="legal-name" className={err("legal_name") ? "border-destructive" : ""} />
              {err("legal_name") && <p className="text-xs text-destructive">{t(err("legal_name"))}</p>}
            </Field>
            <Field label="Identificación fiscal (RFC, SSN/EIN, NIF…)">
              <Input value={form.tax_id} onChange={(e) => setForm((f) => ({ ...f, tax_id: e.target.value }))} data-testid="tax-id" />
            </Field>
            <Field label="Dirección fiscal">
              <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="address" />
            </Field>
          </div>

          <div className="panel p-4 space-y-3" data-testid="tax-form-section">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t("Formulario fiscal requerido:")} <span className="text-primary" data-testid="tax-form-name">{formName}</span></h3>
              <TaxBadge status={p?.tax_form_type === formName ? p?.tax_form_status : "missing"} testId="tax-form-status" />
            </div>
            <p className="text-xs text-muted-foreground">{isUS ? t("Como residente de EE. UU. debes entregar el formulario W-9 del IRS.") : t("Como residente fuera de EE. UU. debes entregar el {form} del IRS para aplicar la tasa de tu país.", { form: formName })}</p>
            <div className="flex flex-wrap items-center gap-3">
              <a href={formUrl} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="tax-form-download">{t("Descargar {form} (PDF del IRS)", { form: formName })} <ExternalLink className="h-3 w-3" /></a>
            </div>
            <FileUpload kind="tax_form" accept="application/pdf,image/jpeg,image/png" label={t("Subir {form} firmado", { form: formName })} value={form.tax_form_file_id} filename={taxFile || (p?.tax_form_file_id && p?.tax_form_type === formName ? `${formName} ${t("enviado")}` : null)}
              onUploaded={(rec) => { setTaxFile(rec.original_filename); setForm((f) => ({ ...f, tax_form_file_id: rec.id })); }} onClear={() => { setTaxFile(null); setForm((f) => ({ ...f, tax_form_file_id: null })); }} testId="tax-form-upload" compact />
            {p?.tax_form_note && <p className="text-xs text-amber-300" data-testid="tax-form-note">{p.tax_form_note}</p>}
          </div>

          <div className="panel p-4 space-y-3" data-testid="bank-section">
            <h3 className="font-display font-semibold flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> {t("Cuenta para transferencia")}</h3>
            <p className="text-xs text-muted-foreground">{t("Solo pagamos por transferencia bancaria. Los campos cambian según el país seleccionado.")}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {country?.fields.map((f) => (
                <Field key={f.key} label={L(f)} className={f.key === "beneficiary_name" || f.key === "bank_address" ? "sm:col-span-2" : ""}>
                  {f.options ? (
                    <SimpleSelect value={form.bank[f.key]} onChange={(v) => setBank(f.key, v)} options={f.options.map((o) => [o, o === "checking" ? "Cheques / corriente" : "Ahorros"])} testId={`bank-${f.key}`} />
                  ) : (
                    <Input value={form.bank[f.key] || ""} onChange={(e) => setBank(f.key, e.target.value)} data-testid={`bank-${f.key}`} className={err(f.key) ? "border-destructive" : ""} />
                  )}
                  {err(f.key) && <p className="text-xs text-destructive">{t(err(f.key))}</p>}
                </Field>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-payment-profile">{t(saving ? "Guardando…" : "Guardar datos de pago")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
